import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, KeyRound, RotateCcw, Save, ShieldAlert } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { config } from "@/api/endpoints";
import type { SettingRow } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorState, FormError, Skeleton, Spinner } from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import { Select, ToggleGroup } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/utils";

export default function Config() {
  const { can } = useAuth();
  const [historyKey, setHistoryKey] = React.useState<string | null>(null);

  const query = useQuery({ queryKey: ["config"], queryFn: config.list });

  const grouped = React.useMemo(() => {
    const map = new Map<string, SettingRow[]>();
    for (const row of query.data ?? []) {
      const list = map.get(row.group) ?? [];
      list.push(row);
      map.set(row.group, list);
    }
    return [...map.entries()];
  }, [query.data]);

  return (
    <>
      <PageHeader
        title="Configuration"
        description="Runtime settings, applied across all API workers without a redeploy."
        actions={
          <Button variant="outline" onClick={() => setHistoryKey("")}>
            <History />
            Change history
          </Button>
        }
      />

      <Card className="mb-4 border-[color-mix(in_srgb,var(--status-warning)_40%,transparent)] bg-[color-mix(in_srgb,var(--status-warning)_7%,transparent)]">
        <CardContent className="flex gap-3 p-4">
          <ShieldAlert
            className="mt-px size-4 shrink-0"
            style={{ color: "var(--status-warning)" }}
          />
          <div className="text-xs">
            <p className="font-medium">
              Infrastructure settings are not editable here, by design.
            </p>
            <p className="mt-1 text-muted-foreground">
              <code>DATABASE_URL</code>, <code>REDIS_URL</code>,{" "}
              <code>JWT_SECRET</code>, <code>SECRET_ENCRYPTION_KEY</code> and{" "}
              <code>APP_ENV</code> come from the environment file only — the API
              needs them before it can reach the database. Change those on the
              server and restart.
            </p>
          </div>
        </CardContent>
      </Card>

      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <div className="space-y-4">
          {grouped.map(([group, rows]) => (
            <Card key={group}>
              <CardHeader>
                <CardTitle>{group}</CardTitle>
                <CardDescription>
                  {rows.length} setting{rows.length === 1 ? "" : "s"}
                </CardDescription>
              </CardHeader>
              <CardContent className="divide-y divide-border p-0">
                {rows.map((row) => (
                  <SettingEditor
                    key={row.key}
                    row={row}
                    canWrite={can("config.write")}
                    onShowHistory={() => setHistoryKey(row.key)}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {historyKey !== null && (
        <HistoryDialog
          settingKey={historyKey || undefined}
          onClose={() => setHistoryKey(null)}
        />
      )}
    </>
  );
}

function SettingEditor({
  row,
  canWrite,
  onShowHistory,
}: {
  row: SettingRow;
  canWrite: boolean;
  onShowHistory: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  // Secrets are write-only: the API returns a mask, so the field always starts
  // empty and only sends something when the operator actually types a new one.
  const [value, setValue] = React.useState(row.is_secret ? "" : row.value);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setValue(row.is_secret ? "" : row.value);
  }, [row.value, row.is_secret]);

  const dirty = row.is_secret ? value.length > 0 : value !== row.value;

  const save = useMutation({
    mutationFn: () => config.update(row.key, coerce(row, value)),
    onSuccess: (result) => {
      toast.success(
        `${row.label} updated`,
        result.requires_restart
          ? "Takes effect after the API restarts."
          : "Applied to all workers.",
      );
      if (row.is_secret) setValue("");
      void queryClient.invalidateQueries({ queryKey: ["config"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not save the setting."),
  });

  const reset = useMutation({
    mutationFn: () => config.reset(row.key),
    onSuccess: () => {
      toast.success(`${row.label} reset`, "The .env value applies after a restart.");
      void queryClient.invalidateQueries({ queryKey: ["config"] });
    },
    onError: (err) =>
      toast.error(
        "Could not reset",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  return (
    <div className="flex flex-wrap items-start gap-4 p-4">
      <div className="min-w-[13rem] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={row.key} className="text-sm">
            {row.label}
          </Label>
          {row.is_secret && (
            <Badge variant="outline">
              <KeyRound className="size-3" />
              Secret
            </Badge>
          )}
          {row.requires_restart && <Badge variant="warning">Needs restart</Badge>}
          {row.is_overridden && <Badge variant="good">Overridden</Badge>}
        </div>
        <p className="mt-0.5 tabular text-[11px] text-muted-foreground">{row.key}</p>
        {row.description && (
          <p className="mt-1 text-xs text-muted-foreground">{row.description}</p>
        )}
        {row.is_secret && (
          <p className="mt-1 text-xs text-muted-foreground">
            Current value:{" "}
            <span className="tabular">{row.is_set ? row.value : "not set"}</span> —
            it cannot be read back, only replaced.
          </p>
        )}
      </div>

      <div className="flex w-full max-w-sm flex-col gap-2">
        <SettingInput row={row} value={value} onChange={setValue} disabled={!canWrite} />
        <FormError message={error} />
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => {
                setError(null);
                save.mutate();
              }}
            >
              {save.isPending ? <Spinner /> : <Save />}
              Save
            </Button>
            {row.is_overridden && (
              <Button
                size="sm"
                variant="outline"
                disabled={reset.isPending}
                onClick={() => reset.mutate()}
              >
                <RotateCcw />
                Reset
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onShowHistory}>
              <History />
              History
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Send the shape the API's registry expects for this value type. */
function coerce(row: SettingRow, raw: string): unknown {
  if (row.value_type === "BOOLEAN") return raw === "true";
  if (row.value_type === "INTEGER") return Number(raw);
  return raw;
}

function SettingInput({
  row,
  value,
  onChange,
  disabled,
}: {
  row: SettingRow;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  if (row.value_type === "BOOLEAN") {
    return (
      <ToggleGroup
        value={value === "true" || value === "True" ? "true" : "false"}
        onValueChange={onChange}
        options={[
          { label: "Enabled", value: "true" },
          { label: "Disabled", value: "false" },
        ]}
      />
    );
  }

  if (row.choices?.length) {
    return (
      <Select
        value={value}
        onValueChange={onChange}
        options={row.choices.map((c) => ({ label: c, value: c }))}
        disabled={disabled}
        aria-label={row.label}
      />
    );
  }

  const numeric = row.value_type === "INTEGER" || row.value_type === "DECIMAL";

  return (
    <Input
      id={row.key}
      type={row.is_secret ? "password" : numeric ? "number" : "text"}
      inputMode={numeric ? "numeric" : undefined}
      min={row.min_value ?? undefined}
      max={row.max_value ?? undefined}
      step={row.value_type === "DECIMAL" ? "0.01" : undefined}
      value={value}
      disabled={disabled}
      autoComplete={row.is_secret ? "new-password" : "off"}
      placeholder={
        row.is_secret ? "Enter a new value to replace it" : row.label
      }
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function HistoryDialog({
  settingKey,
  onClose,
}: {
  settingKey?: string;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ["config-history", settingKey ?? "all"],
    queryFn: () => config.history(settingKey),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent side="right">
        <DialogHeader>
          <DialogTitle>
            {settingKey ? `History — ${settingKey}` : "Configuration history"}
          </DialogTitle>
          <DialogDescription>
            Secret values are never recorded here — only the fact that they changed.
          </DialogDescription>
        </DialogHeader>

        {query.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data?.length ? (
          <ul className="space-y-2">
            {query.data.map((h) => (
              <li key={h.id} className="rounded-md border border-border p-3 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular font-medium">{h.key}</span>
                  {h.is_secret && <Badge variant="outline">Secret</Badge>}
                  <span className="ml-auto text-muted-foreground">
                    {formatDateTime(h.created_at)}
                  </span>
                </div>
                <p className="mt-1.5 break-words">
                  {h.is_secret ? (
                    <span className="text-muted-foreground">Value replaced</span>
                  ) : (
                    <>
                      <span className="text-muted-foreground">
                        {h.old_value ?? "unset"}
                      </span>
                      {" → "}
                      <span className="font-medium">{h.new_value ?? "reset"}</span>
                    </>
                  )}
                </p>
                {h.reason && (
                  <p className="mt-1 italic text-muted-foreground">{h.reason}</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-8 text-center text-xs text-muted-foreground">
            No changes recorded yet.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
